import 'regenerator-runtime/runtime';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { Input, Icon } from 'semantic-ui-react';
import { debounce, throttle } from '../../../helpers/utility';
import Loader from '../../../components/loader';
import { getDataSource, getDataSourceValues, getExternalDataSourceValues as getExternalDataSourceValues } from '../../../helpers/DataSourceUtility';
import CalculationRunner from '../../../calculation/CalculationRunner';
import './DatabaseModal.scss';
import GfModal from '../../../gfcomponents/GfModal/GfModal';
import { DataSourceOrigin, DataSourceSearchOption } from '../../../helpers/constants';
import { DataSource } from '../../../datasourceeditor/datasourceeditor-types';
import { Radio } from 'semantic-ui-react';

type DatabaseModalState = {
  searchQuery?: string;
  dataSource?: DataSource;
  dataSourceLoaded: boolean;
  dataSourceValuesLoaded: boolean;
  dataSourceValuesLoading: boolean;
  dataSourceValues: any[];
  errorMessage?: string;
  searchOption?: string;
};

export interface DatabaseModalProps {
  formId?: string;
  form?: TypeMePlease;
  template?: TypeMePlease;
  value?: string;
  allowOther?: boolean;
  onChanged?: AnyFunc;
  open?: boolean;
  onClose?: AnyFunc;
  dataSourceName?: string;
  dataSourceOrigin?: DataSourceOrigin;
  displayColumn?: string;
  filter?: string;
  tableName?: string;
}

function mapStateToProps(state) {
  return {
    formId: state.formeditor.form?.id,
    template: state.formeditor.template,
    form: state.formeditor.form
  };
}

//@ts-ignore - FIX
@connect(mapStateToProps, {})
export default class DatabaseModal extends PureComponent<DatabaseModalProps, DatabaseModalState> {
  private calcRunner: CalculationRunner;
  private formValues: TypeMePlease;
  private debouncedAutocomplete: (searchTerm: string) => void;

  constructor(props) {
    super(props);

    this.handleDialogModalClose = this.handleDialogModalClose.bind(this);
    this.handleDropdownChanged = this.handleDropdownChanged.bind(this);
    this.handleSearchBoxChanged = this.handleSearchBoxChanged.bind(this);
    this.handleSearchBoxKeyUp = this.handleSearchBoxKeyUp.bind(this);
    this.handlesearchOptionChange = this.handlesearchOptionChange.bind(this);
    this.handleDoneButtonClicked = this.handleDoneButtonClicked.bind(this);
    this.handleCancelButtonClicked = this.handleCancelButtonClicked.bind(this);
    this.handleClearSearch = this.handleClearSearch.bind(this);
    this.getAutocompleteValues = throttle(this.getAutocompleteValues, 500).bind(this);
    this.handleClearButtonClicked = this.handleClearButtonClicked.bind(this);
    this.debouncedAutocomplete = debounce(this.getAutocompleteValues, 500).bind(this);

    this.state = {
      searchQuery: this.props.value,
      dataSource: null,
      dataSourceLoaded: false,
      dataSourceValuesLoaded: false,
      dataSourceValuesLoading: false,
      dataSourceValues: [],
      errorMessage: null,
      searchOption: 'Contains'
    };
  }

  async componentDidMount() {
    if (this.props.filter) {
      this.calcRunner = new CalculationRunner(this.props.formId, this.props.template);
      await this.calcRunner.init();
      this.formValues = this.props.form.values;
    }
    this.init(this.props.dataSourceName, this.props.displayColumn, this.props.allowOther ? this.props.value : '');
  }

  async getAutocompleteValues(searchTerm) {
    this.setState({ dataSourceValuesLoading: true });

    if (!this.state.dataSource) {
      return;
    }

    const dataSourceValues =
      this.props.dataSourceOrigin && this.props.dataSourceOrigin !== DataSourceOrigin.Client
        ? await getExternalDataSourceValues(
            this.state.dataSource.name,
            this.props.displayColumn,
            searchTerm,
            this.props.dataSourceOrigin,
            this.props.filter,
            this.calcRunner,
            this.formValues,
            this.props.tableName
          )
        : await getDataSourceValues(
            this.state.dataSource,
            this.props.displayColumn,
            searchTerm,
            this.props.filter,
            this.calcRunner,
            this.formValues,
            this.props.tableName,
            [this.state.searchOption]
          );
    this.setState({ dataSourceValues, dataSourceValuesLoading: false, dataSourceValuesLoaded: true });
  }

  async init(dataSourceName, displayColumn, searchTerm) {
    if (!dataSourceName) {
      //the datasource name is not set, this must mean it was never set on the template.
      //we are going to treat this the same as if it was missing i.e a http status code of 404
      const missingDatasourceStatusCode = 404;
      const errorMessage = this.getErrorMessageFromResponse(missingDatasourceStatusCode);
      this.setState({ errorMessage });
      return;
    }

    this.startLoading();
    try {
      let dataSource = null;
      if (this.props.dataSourceOrigin && this.props.dataSourceOrigin !== DataSourceOrigin.Client) {
        dataSource = { name: dataSourceName, id: '', columnNames: null, copyLocal: null, createdDate: null, lastUpdatedDate: null, publicShareable: null };
        this.setState({
          dataSource,
          dataSourceLoaded: true
        });
      } else {
        dataSource = await this.getDataSource(dataSourceName);
        if (!dataSource) {
          return;
        }
      }

      await this.getAndSetDataSourceValues(dataSource, displayColumn, searchTerm, this.props.dataSourceOrigin);
    } catch (error) {
      this.handleError(error);
    } finally {
      this.stopLoading();
    }
  }

  async getDataSource(dataSourceName) {
    try {
      const dataSource = await getDataSource(dataSourceName);
      if (dataSource) {
        this.setState({ dataSource, dataSourceLoaded: true });
      }
      return dataSource;
    } catch (error) {
      this.handleError(error);
      return null;
    }
  }

  async getAndSetDataSourceValues(dataSource, displayColumn, searchTerm, dataSourceOrigin: DataSourceOrigin) {
    const dataSourceValues =
      dataSourceOrigin && dataSourceOrigin !== DataSourceOrigin.Client
        ? await getExternalDataSourceValues(
            dataSource.name,
            displayColumn,
            searchTerm,
            dataSourceOrigin,
            this.props.filter,
            this.calcRunner,
            this.formValues,
            this.props.tableName
          )
        : await getDataSourceValues(dataSource, displayColumn, searchTerm, this.props.filter, this.calcRunner, this.formValues, this.props.tableName, [
            this.state.searchOption
          ]);
    this.setState({ dataSourceValues, dataSourceValuesLoaded: true });
  }

  handleError(error) {
    if (!error.response) {
      return;
    }

    const errorMessage = this.getErrorMessageFromResponse(error?.response?.status, error?.response?.data?.error);
    this.setState({ errorMessage });
  }

  getErrorMessageFromResponse(status, error?) {
    if (status === 404 && this.props.allowOther) {
      return (
        <div>
          Datasource not found.
          <br />
          <br />
          Please enter a custom value or contact the admin of your account.
        </div>
      );
    }
    if (status === 404) {
      return 'Error: Datasource not found. Please contact the admin of your account.';
    }
    if (error) {
      return error;
    }
    return 'Error Loading';
  }

  startLoading() {
    this.setState({ dataSourceValuesLoading: true });
  }

  stopLoading() {
    this.setState({ dataSourceValuesLoading: false });
  }

  handleDialogModalClose() {
    this.props.onClose();
  }

  handleDropdownChanged(event) {
    this.props.onChanged(event.target.textContent);
  }

  handleSearchBoxChanged(value) {
    this.setState({
      searchQuery: value
    });

    this.debouncedAutocomplete(value);
  }

  handleSearchBoxKeyUp(event) {
    if (event.keyCode === 13) {
      const matchingValue = this.state.dataSourceValues.find((p) => {
        return p.toUpperCase().startsWith(this.state.searchQuery.toUpperCase());
      });
      let valueToSave = this.state.searchQuery;
      if (!this.props.allowOther) {
        if (matchingValue) {
          valueToSave = matchingValue;
        } else {
          return;
        }
      }

      this.props.onChanged(valueToSave);
    }
  }

  handleDoneButtonClicked() {
    let valueToSave = this.state.searchQuery;

    const matchingValue = this.state.dataSourceValues.find((p) => {
      return p.toUpperCase() === this.state.searchQuery.toUpperCase();
    });
    if (matchingValue) {
      valueToSave = matchingValue;
    }
    this.props.onChanged(valueToSave);
    this.props.onClose();
  }

  handleCancelButtonClicked() {
    this.props.onClose();
  }

  handleClearSearch() {
    this.getAutocompleteValues('');
    this.setState({ searchQuery: '' });
  }

  handleClearButtonClicked() {
    this.props.onChanged('');
  }

  handlesearchOptionChange(value) {
    this.setState({ searchOption: value });

    // Only trigger search if there's a search query
    if (this.state.searchQuery && this.state.searchQuery.trim() !== '') {
      this.getAutocompleteValues(this.state.searchQuery);
    }
  }

  render() {
    let itemList = null;
    let searchBox = null;
    let searchOptions = null;

    searchBox = (
      <Input
        fluid
        placeholder={this.props.allowOther || this.props.allowOther === null ? 'Enter Value' : 'Find Value'}
        icon={<Icon name="close" className="big clear-search" link onClick={this.handleClearSearch} />}
        className="database-field-input"
        onChange={(e) => this.handleSearchBoxChanged(e.target.value)}
        onKeyUp={this.handleSearchBoxKeyUp}
        value={this.state.searchQuery}
      />
    );

    // Search options are for PDS only
    searchOptions = (
      <div>
        {this.state.dataSource?.isPds && (
          <div className="search-options">
            <Radio
              name="searchOption"
              label={DataSourceSearchOption.Contains}
              value={DataSourceSearchOption.Contains}
              checked={this.state.searchOption === DataSourceSearchOption.Contains}
              onChange={(e, { value }) => this.handlesearchOptionChange(value)}
            />
            <Radio
              name="searchOption"
              label={DataSourceSearchOption.StartsWith}
              value={DataSourceSearchOption.StartsWith}
              checked={this.state.searchOption === DataSourceSearchOption.StartsWith}
              onChange={(e, { value }) => this.handlesearchOptionChange(value)}
            />
            <Radio
              name="searchOption"
              label={DataSourceSearchOption.EndsWith}
              value={DataSourceSearchOption.EndsWith}
              checked={this.state.searchOption === DataSourceSearchOption.EndsWith}
              onChange={(e, { value }) => this.handlesearchOptionChange(value)}
            />
          </div>
        )}
      </div>
    );

    if (this.state.dataSourceValues && this.state.dataSourceValuesLoaded && this.state.dataSourceLoaded) {
      itemList = this.state.dataSourceValues.map((item) => (
        <li key={item} className={`item ${item === this.props.value ? 'selected' : ''}`}>
          <button onClick={this.handleDropdownChanged} className="database-field-item">
            {item}
          </button>
        </li>
      ));
    }

    return (
      <GfModal
        title="Select an item"
        className="database-modal"
        scrolling
        primaryText={this.props.allowOther ? 'Done' : undefined}
        secondaryText="Clear"
        tertiaryText="Cancel"
        open={this.props.open}
        onPrimaryClick={this.handleDoneButtonClicked}
        onSecondaryClick={this.handleClearButtonClicked}
        onTertiaryClick={this.handleCancelButtonClicked}
        onClose={this.handleDialogModalClose}
        semanticUiModalProps={{ size: 'small', closeOnDimmerClick: true }}
      >
        {searchOptions}
        {searchBox}
        <div className="scrollBox">
          <Loader isLoading={this.state.dataSourceValuesLoading} />
          <ul>{itemList}</ul>
          {this.state.errorMessage && !this.props.allowOther ? <div className="database-field-error">{this.state.errorMessage}</div> : null}
          {this.state.errorMessage && this.props.allowOther ? <div className="database-not-found">{this.state.errorMessage}</div> : null}
        </div>
      </GfModal>
    );
  }
}
